import React, { useState, useCallback } from 'react';
import Chart from 'react-apexcharts';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import ResponsiveTable from './ResponsiveTable';

const UNIFIED_BAR_OPTIONS = (categories, id) => ({
  chart: { id, toolbar: { show: false }, background: 'transparent' },
  theme: { mode: 'dark' },
  xaxis: {
    categories,
    labels: { style: { fontSize: '12px', colors: '#94a3b8' } },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  yaxis: {
    labels: { show: false },
    axisTicks: { show: false },
    axisBorder: { show: false },
  },
  dataLabels: {
    enabled: true,
    formatter: (val) => val.toLocaleString(),
    offsetY: -20,
    style: { fontSize: '11px', colors: ['#94a3b8'] },
  },
  plotOptions: {
    bar: { dataLabels: { position: 'top' }, borderRadius: 4 },
  },
  fill: { colors: ['#0ea5e9'] },
  grid: { borderColor: 'rgba(255,255,255,0.05)', padding: { left: 10, right: 0 } },
  tooltip: { theme: 'dark', y: { formatter: (val) => val.toLocaleString() } },
  responsive: [
    {
      breakpoint: 640,
      options: {
        chart: { height: 250, toolbar: { show: false } },
        yaxis: [{ labels: { show: false }, axisTicks: { show: false }, axisBorder: { show: false } }],
        grid: { padding: { left: 24 } },
      },
    },
  ],
});

function ByArtistsTable({ data, totalCount, currentPage, pageSize, me }) {
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);

  const handleArtistClick = useCallback(
    async (artistId, name) => {
      setSelectedArtist(name);
      try {
        const res = await axios.get(`/artist/getMonthlyArtistAmounts`, {
          params: {
            artistId,
            clientId: me?.id,
            adminYn: me?.adminYn,
          },
        });
        setMonthlyData(res.data);
      } catch (error) {
        console.error('차트 데이터 불러오기 실패:', error);
      }
    },
    [me]
  );

  const columns = React.useMemo(() => {
    if (!me) return [];

    return [
      {
        Header: () => <div className="whitespace-nowrap text-center">순번</div>,
        id: 'row',
        Cell: ({ row }) => (
          <div className="text-center whitespace-nowrap">
            {totalCount - ((currentPage - 1) * pageSize + row.index)}
          </div>
        ),
      },
      {
        Header: () => (
          <div className="whitespace-nowrap min-w-[48px] text-center">사진</div>
        ),
        accessor: 'artist.image',
        Cell: ({ value }) => {
          const imageUrl =
            value ||
            'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg';
          return (
            <img
              src={imageUrl}
              alt="artist"
              className="w-12 h-12 rounded-full object-cover mx-auto border border-white/[0.08]"
            />
          );
        },
      },
      {
        Header: '아티스트명',
        accessor: 'artist.nameKr',
        Cell: ({ value, row }) => {
          const artist = row.original?.artist;
          return (
            <b
              className="text-sky-600 cursor-pointer"
              onClick={() => {
                if (artist?.id) {
                  handleArtistClick(artist.id, value);
                } else {
                  console.warn('artist 정보 없음:', row.original);
                }
              }}>
              {value}
            </b>
          );
        },
      },
      ...(me?.adminYn === 'Y'
        ? [
            {
              Header: '권리사',
              accessor: 'artist.displayClient',
              Cell: ({ value }) => <span>{value || '-'}</span>,
            },
          ]
        : []),
      {
        Header: '총 히트수',
        accessor: 'artistHitCount',
        Cell: ({ value }) => Number(value).toLocaleString(),
      },
      {
        Header: '총 정산액',
        accessor: 'artistAmount',
        Cell: ({ value }) => <b>{Number(value).toLocaleString()}</b>,
      },
    ];
  }, [totalCount, currentPage, pageSize, handleArtistClick, me]);

  return (
    <>
      <ResponsiveTable
        columns={columns}
        data={data}
        visibleOnMobile={[
          'row',
          'artist.image',
          'artist.nameKr',
          'artistAmount',
        ]}
      />

      {selectedArtist && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={() => setSelectedArtist(null)}>
          <div
            className="bg-[#1a2b42] p-6 rounded-xl shadow-xl border border-white/10 w-full max-w-2xl relative"
            onClick={(e) => e.stopPropagation()}>
            <button
              className="absolute top-2 right-2 text-slate-400 hover:text-white"
              onClick={() => setSelectedArtist(null)}>
              <FontAwesomeIcon icon={faXmark} size="lg" />
            </button>
            <h2 className="text-xl font-bold mb-4 text-white">
              "{selectedArtist}" 최근 1년 정산 그래프
            </h2>
            <Chart
              type="bar"
              width="100%"
              height="300"
              series={[
                {
                  name: '월별 정산액',
                  data: monthlyData.map((item) => item.amount),
                },
              ]}
              options={UNIFIED_BAR_OPTIONS(
                monthlyData.map((item) => item.month),
                'artist-month-chart'
              )}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default ByArtistsTable;
